import { FC, useState } from 'react';
import { Box, Chip, Divider, IconButton, Popover, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { observer } from 'mobx-react-lite';

import { SingularKeys, Trans, useTrans } from '@libs/i18n';
import { Assessment, DomainComparisonEntry } from '@libs/types';
import { ChartBarIcon, InfoIcon } from '@phosphor-icons/react';

import { DomainResultViewModel } from '../../types';

type FilterMode = 'worst5' | 'best5';

interface DomainBarChartProps {
  domainResults: DomainResultViewModel[];
  domainComparisonMap?: Map<string, DomainComparisonEntry>;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
  height?: number;
}

const ChartEmpty: FC<{ titleKey: SingularKeys; subtitleKey: SingularKeys }> = observer(({ titleKey, subtitleKey }) => {
  const theme = useTheme();
  const { t } = useTrans();
  return (
    <Box
      sx={{
        height: 260,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.text.disabled,
          mb: 0.5,
        }}
      >
        <ChartBarIcon size={22} />
      </Box>
      <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
        {t(titleKey)}
      </Typography>
      <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', maxWidth: 280 }}>
        {t(subtitleKey)}
      </Typography>
    </Box>
  );
});

export const DomainBarChart: FC<DomainBarChartProps> = observer(
  ({ domainResults, domainComparisonMap, activeVersion, baselineVersion, height = 260 }) => {
    const theme = useTheme();
    const { t } = useTrans();
    const [filter, setFilter] = useState<FilterMode>('worst5');
    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

    const isComparing = !!baselineVersion && !!domainComparisonMap;

    // Include every domain marked applicable. Skipped domains keep their slot
    // but are scored at 0% — so Worst 5 / Best 5 always fills 5 bars when
    // possible, padding from the highest-weighted skipped domains.
    const inScope = domainResults.filter((d) => d.isApplicable);

    if (inScope.length === 0) {
      return (
        <ChartEmpty
          titleKey="assessments.overview.charts.empty.title"
          subtitleKey="assessments.overview.charts.empty.subtitle"
        />
      );
    }

    type Ranked = { domain: DomainResultViewModel; score: number; isMissing: boolean };
    const ranked: Ranked[] = inScope.map((d) => ({
      domain: d,
      score: d.isSkipped ? 0 : Math.round(d.maturityScore ?? 0),
      isMissing: d.isSkipped,
    }));

    // Ties broken by weight DESC — higher-weight (more important) domains win
    // the slot, especially relevant when padding with missing 0% rows.
    const sorted = filter === 'worst5'
      ? [...ranked].sort(
          (a, b) => (a.score - b.score) || ((b.domain.weight ?? 0) - (a.domain.weight ?? 0)),
        )
      : [...ranked].sort(
          (a, b) => (b.score - a.score) || ((b.domain.weight ?? 0) - (a.domain.weight ?? 0)),
        );

    const displayed = sorted.slice(0, 5);
    const missingTotal = inScope.filter((d) => d.isSkipped).length;

    const labels = displayed.map(({ domain, isMissing }) => {
      const raw = (domain.domainName ?? '').trim() || ' ';
      const truncated = raw.length > 14 ? `${raw.slice(0, 12)}…` : raw;
      // Prefix skipped/missing domains with a small marker so users can tell
      // them apart from genuine low-score bars.
      return isMissing ? `○ ${truncated}` : truncated;
    });

    const currentScores = displayed.map(({ score }) => score);
    const baselineScores = displayed.map(({ domain }) => {
      const entry = domainComparisonMap?.get(domain.domainId);
      return Math.round(entry?.baselineResults?.maturity?.percentage ?? 0);
    });

    const scoreColor = filter === 'worst5' ? theme.palette.error.light : theme.palette.success.main;
    const baselineColor = theme.palette.grey[400];

    const series = isComparing
      ? [
          {
            data: currentScores,
            label: activeVersion
              ? t('assessments.overview.charts.versionCurrent', { version: activeVersion.version })
              : t('assessments.overview.charts.versionCurrent', { version: '' }),
            color: scoreColor,
            valueFormatter: (v: number | null) => `${v ?? 0}%`,
          },
          {
            data: baselineScores,
            label: baselineVersion
              ? t('assessments.overview.charts.versionBaseline', { version: baselineVersion.version })
              : t('assessments.overview.charts.versionBaseline', { version: '' }),
            color: baselineColor,
            valueFormatter: (v: number | null) => `${v ?? 0}%`,
          },
        ]
      : [
          {
            data: currentScores,
            label: t('assessments.overview.charts.stat.maturityScore') || 'Maturity Score',
            valueFormatter: (v: number | null) => `${v ?? 0}%`,
            color: scoreColor,
          },
        ];

    // Dynamically widen the category gap when there are few bars so a single bar
    // doesn't stretch across the entire chart width.
    const categoryGapRatio = displayed.length <= 1
      ? 0.85
      : displayed.length === 2
      ? 0.7
      : displayed.length === 3
      ? 0.55
      : 0.4;

    const chartHeight = height - 20; // leaves some padding top and bottom
    const chartMargin = {
      top: 12,
      right: 16,
      bottom: isComparing ? 56 : 32,
      left: 40,
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', minHeight: 0 }}>
        {/* Left Sidebar: Title, Subtitle, worst/best chips, layout chips */}
        <Box
          sx={{
            width: 180,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            p: 2,
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            overflow: 'auto',
          }}
        >
          {/* Title & Description */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.25 }}>
                {t('assessments.overview.charts.domainScoresTitle') || 'Domain Scores'}
              </Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', lineHeight: 1.35, fontSize: '0.72rem' }}>
                {t('assessments.overview.charts.domainScoresSubtitle') || 'Readiness per domain'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' }, mt: -0.25 }}>
              <InfoIcon size={16} />
            </IconButton>
          </Box>

          <Popover
            open={Boolean(infoAnchor)}
            anchorEl={infoAnchor}
            onClose={() => setInfoAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { maxWidth: 280, p: 2, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${theme.palette.grey[200]}` } } }}
          >
            <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 1 }}>
              <Trans i18nKey="assessments.overview.charts.howToRead" />
            </Typography>
            <Divider sx={{ mb: 1.25, borderColor: 'grey.100' }} />
            {[
              { label: 'Bar length', desc: 'Longer bar = higher readiness score. 100% means fully ready for that domain.' },
              { label: 'Worst 5 / Best 5', desc: 'Switch between the 5 lowest-scoring domains (priorities) and the 5 highest-scoring (strengths).' },
              { label: 'Skipped domains (○)', desc: 'Domains marked with a circle were not assessed — they score 0% and appear in Worst 5.' },
              { label: 'Version comparison', desc: 'When comparing versions a second bar appears showing the baseline score side by side.' },
            ].map(({ label, desc }) => (
              <Box key={label} sx={{ mb: 1.25 }}>
                <Typography variant="textXS_semiBold" sx={{ color: 'text.primary', display: 'block' }}>{label}</Typography>
                <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>{desc}</Typography>
              </Box>
            ))}
          </Popover>

          <Divider sx={{ borderColor: 'grey.100' }} />

          {/* Filter Options: Worst 5 / Best 5 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="textXS_semiBold" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>
              {t('assessments.overview.charts.filter') || 'Filter'}
            </Typography>
            
            <Chip
              label={t('assessments.overview.charts.worstFive') || 'Worst 5 Domains'}
              size="small"
              onClick={() => setFilter('worst5')}
              sx={{
                fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer',
                backgroundColor: filter === 'worst5' ? theme.palette.error.main : theme.palette.grey[100],
                color: filter === 'worst5' ? 'white' : theme.palette.text.secondary,
                border: `1px solid ${filter === 'worst5' ? theme.palette.error.main : theme.palette.grey[300]}`,
                width: '100%',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: filter === 'worst5' ? theme.palette.error.dark : theme.palette.grey[200],
                },
              }}
            />

            <Chip
              label={t('assessments.overview.charts.bestFive') || 'Best 5 Domains'}
              size="small"
              onClick={() => setFilter('best5')}
              sx={{
                fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer',
                backgroundColor: filter === 'best5' ? theme.palette.success.main : theme.palette.grey[100],
                color: filter === 'best5' ? 'white' : theme.palette.text.secondary,
                border: `1px solid ${filter === 'best5' ? theme.palette.success.main : theme.palette.grey[300]}`,
                width: '100%',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: filter === 'best5' ? theme.palette.success.dark : theme.palette.grey[200],
                },
              }}
            />
          </Box>

          {/* Metadata */}
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', fontSize: '0.7rem' }}>
              {t('assessments.overview.charts.domainsShown', {
                count: displayed.length,
                shown: displayed.length,
                total: inScope.length,
              })}
            </Typography>
            {missingTotal > 0 && (
              <Typography variant="textXS_regular" sx={{ color: theme.palette.warning.dark, fontSize: '0.68rem' }}>
                {t('assessments.overview.charts.notAssessedCount', { count: missingTotal })}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Area: Bar Chart */}
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <BarChart
            height={chartHeight}
            xAxis={[{
              data: labels,
              scaleType: 'band',
              categoryGapRatio,
              barGapRatio: 0.15,
            }]}
            yAxis={[{
              min: 0,
              max: 100,
              tickInterval: [0, 25, 50, 75, 100],
              valueFormatter: (v: number | null) => `${v ?? 0}%`,
            }]}
            series={series}
            margin={chartMargin}
            hideLegend={!isComparing}
            slotProps={{
              legend: {
                position: { vertical: 'bottom', horizontal: 'center' },
              },
            }}
            sx={{
              '& .MuiChartsAxis-line':      { stroke: theme.palette.grey[200] },
              '& .MuiChartsAxis-tick':      { stroke: theme.palette.grey[200] },
              '& .MuiChartsGrid-line':      { stroke: theme.palette.grey[100] },
              '& .MuiChartsAxis-tickLabel': { fontSize: '9px', fill: theme.palette.text.secondary },
            }}
            grid={{ horizontal: true }}
          />
        </Box>
      </Box>
    );
  },
);
